/**
 * The property key a computed subscript actually lands on.
 *
 * This app indexes its lookup tables — email templates, vendor configs, program
 * budgets, per-member selections — with values that are not statically strings: a
 * Semantic UI `DropdownProps['value']` (`string | number | boolean | (string |
 * number | boolean)[] | undefined`), an optional id off a fetched row, a `null`
 * patient id in the roster-wide views.
 *
 * A bracket subscript has always coerced those to a string before the lookup, so
 * `undefined` has read the key `'undefined'`, `true` the key `'true'`, and an array
 * its comma-joined form. `String()` performs exactly that coercion, for every one of
 * those cases, which is what makes it a narrowing rather than a change: the key
 * computed here is the key the runtime was already computing.
 *
 * Deliberately NOT `.toString()`, which is the idiom used elsewhere in this app for
 * the same union. The two agree on arrays, booleans and numbers but not on absence:
 * `undefined.toString()` throws a TypeError where `obj[undefined]` reads the
 * `'undefined'` key. That is the whole reason for `String()` here.
 *
 * `String()` is NOT total, and it is not a subscript. Two cases diverge, neither
 * reachable at any current call site — this is a statement of the tool's limits, not
 * a defect report:
 *   - `String(Object.create(null))` THROWS `TypeError: Cannot convert object to
 *     primitive value`, where the subscript it replaces would have worked.
 *   - `String(someSymbol)` returns `'Symbol(x)'`, whereas `obj[someSymbol]` looks up
 *     the Symbol key itself with no coercion at all — so the two read DIFFERENT keys
 *     rather than the same one.
 * Both are ruled out by the union this is applied to (`string | number | boolean |
 * array | null | undefined`), which is why the helper is honest for its actual
 * inputs. It is not a general-purpose key coercion, and widening the parameter past
 * that union would need this comment revisited.
 *
 * This does not make a lookup safe — it makes it honest. A key that misses still
 * yields `undefined`, which is why the tables it indexes are declared with the
 * open-key idiom (`typeof LITERAL & Record<string, Entry | undefined>`) in
 * `components/common/vendorConfig` and `fhir-sdoh/common/constants`.
 */
export const mapKey = (key: unknown): string => String(key);
